import { useEffect, useRef } from "react";
import http from "@/api";

/**
 * useSilentAutoRefresh — polls /api/version and detects a NEW DEPLOYED BUILD.
 *
 * ROOT-CAUSE FIX (June 2026 — «الضغط مرتين وثلاثاً على حسابي»):
 *   The old version applied the pending update with window.location.reload()
 *   on the user's NEXT route navigation. After every deploy, the first tap on
 *   any bottom-nav icon triggered a FULL app reboot: the tap looked dead for
 *   seconds and users tapped 2–4 times. Navigation taps must NEVER reload.
 *
 *   Now the pending update is applied ONLY while the app is hidden
 *   (user switched tabs / locked the phone / minimized the PWA) — completely
 *   invisible to the user. Stale-chunk errors after a deploy are already
 *   handled separately by ChunkErrorBoundary (purge caches + guarded reload).
 */
export default function useSilentAutoRefresh(interval = 120_000) {
  const baselineRef = useRef(null);
  const pendingRef = useRef(false);
  const busyRef = useRef(false);

  // Apply a pending update ONLY while the app is not visible — zero
  // interruption: the new build is there next time the user comes back.
  useEffect(() => {
    const applyIfHidden = () => {
      if (!pendingRef.current) return;
      if (document.visibilityState !== "hidden") return;
      try {
        const now = Date.now();
        const last = Number(sessionStorage.getItem("rg_last_auto_reload") || 0);
        if (now - last < 60_000) return;
        sessionStorage.setItem("rg_last_auto_reload", String(now));
      } catch (e) { /* sessionStorage unavailable — proceed */ }
      pendingRef.current = false;
      window.location.reload();
    };
    document.addEventListener("visibilitychange", applyIfHidden);
    return () => document.removeEventListener("visibilitychange", applyIfHidden);
  }, []);

  useEffect(() => {
    let timer = null;

    const checkOnce = async () => {
      if (busyRef.current || pendingRef.current) return;
      if (document.visibilityState !== "visible") return;
      busyRef.current = true;
      try {
        const { data } = await http.get("/version", { timeout: 5000 });
        const next = data?.hash;
        if (!next || next === "dev") return;
        if (!baselineRef.current) {
          baselineRef.current = next;
          return;
        }
        if (next !== baselineRef.current) {
          baselineRef.current = next;
          pendingRef.current = true; // applied silently while app is hidden
        }
      } catch (e) {
        // Network blip or backend down — just try again later.
      } finally {
        busyRef.current = false;
      }
    };

    const tick = () => {
      checkOnce();
      timer = setTimeout(tick, interval);
    };

    checkOnce();
    timer = setTimeout(tick, interval);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [interval]);
}
